import { MapService } from './services/MapService.js';
import { ElevationService } from './services/ElevationService.js';
import { WindService } from './services/WindService.js';
import { RouteService } from './services/RouteService.js';
import { routeTrackingService } from './services/RouteTrackingService.js';
import { RVProfileService } from './services/RVProfileService.js';
import { GasStationService } from './services/GasStationService.js';
import { UIController } from './controllers/UIController.js';
import { AuthService, signupUnfinished, clearSignupStarted } from './services/AuthService.js';
import { SubscriptionService } from './services/SubscriptionService.js';
import { canPurchaseInApp, showsWebPricing } from './services/purchase.js';
import { playBillingService } from './services/PlayBillingService.js';
import { userProfileService } from './services/UserProfileService.js';
import { setUnitSystem } from './services/units.js';
import { syncInputUnits } from './services/unit-inputs.js';
import { pagePath, w, defaultUnits, applyAccount, applyAccountMarketHint, clearAccountMarket } from './services/market.js';
import { autocompleteService } from './services/AutocompleteService.js';
import { ClearanceService } from './services/ClearanceService.js';
import { RoadQualityService, isSegmentOnRoute } from './services/RoadQualityService.js';
import { CampgroundService } from './services/CampgroundService.js';
import { authFetch } from './services/authFetch.js';
import { pushNotificationService } from './services/PushNotificationService.js';
import { waypointTrackingService } from './services/WaypointTrackingService.js';
import { notificationCenterService } from './services/NotificationCenterService.js';
import { favoritesService } from "./services/FavoritesService.js";
import "./ui/FavoritesPanel.js";
import { savedRoutesService } from "./services/SavedRoutesService.js";
import "./ui/SavedRoutesPanel.js";
import * as units from './services/units.js';
import { initRouteCardLayers } from './ui/RouteCardLayers.js';
import { gradeSegmentsFrom, roadQualitySegmentsFrom } from './services/routeHazards.js';

// Make services available globally
window.RVProfileService = RVProfileService;
window.userProfileService = userProfileService;
window.pushNotificationService = pushNotificationService;
window.waypointTrackingService = waypointTrackingService;
window.notificationCenterService = notificationCenterService;
window.savedRoutesService = savedRoutesService;

// Phase 28: Render notification inbox in hamburger menu
window.renderNotificationInbox = function() {
    const inbox = document.getElementById('notificationInbox');
    if (inbox && notificationCenterService.loaded) {
        inbox.innerHTML = notificationCenterService.renderInbox();
    }
};

// Initialize auth and subscription services globally
const authService = new AuthService();
const subscriptionService = new SubscriptionService();
window.authService = authService;
document.getElementById('finishSignupClose')?.addEventListener('click', () => {
    clearSignupStarted();
    document.getElementById('finishSignupBanner').style.display = 'none';
});

// The site's default (or this device's remembered account); a signed-in
// profile's units replace it once loaded.
setUnitSystem(defaultUnits());
syncInputUnits();

// Upgrade prompts only where Pro can be bought here; the website's pricing
// pages are never linked from inside the app (see services/purchase.js).
if (!canPurchaseInApp()) {
    document.querySelectorAll('.purchase-prompt').forEach((el) => { el.style.display = 'none'; });
}
if (!showsWebPricing()) {
    document.querySelectorAll('.web-pricing-link').forEach((el) => { el.style.display = 'none'; });
}

window.subscriptionService = subscriptionService;

// ═══════════════════════════════════════════════════════════════
// Sign-In Flow: Save Route + Soft Prompt
// Save all route inputs before redirecting to sign in.
// Restore after sign-up/sign-in completes. Auto-trigger Calculate.
// ═══════════════════════════════════════════════════════════════

// Reads the plan form into the same field shape used by both the
// sign-in pending-route flow (localStorage) and the saved-routes flow
// (DynamoDB, via SavedRoutesService) -- one source of truth for what
// counts as "the route's inputs".
window.getRoutePlanInputs = function() {
    const startPlace = window.autocompleteService?.resolvedPlaces?.start;
    const destPlace = window.autocompleteService?.resolvedPlaces?.destination;
    return {
        origin: document.getElementById('start')?.value || '',
        // Capture the actual resolved coordinate (autocomplete selection or
        // GPS fix), not just the display text -- applyRoutePlanInputs() can
        // then pin this exact point later instead of re-geocoding the text,
        // which can land somewhere very different (confirmed: a GPS-sourced
        // origin saved as "Clear Creek County, Colorado" forward-geocodes
        // to the COUNTY CENTROID via Nominatim -- a real place, but not
        // where GPS actually was, and not reliably routable).
        originLat: typeof startPlace?.lat === 'number' ? startPlace.lat : null,
        originLng: typeof startPlace?.lng === 'number' ? startPlace.lng : null,
        // Kept only as a fallback for routes saved before originLat/Lng
        // existed -- applyRoutePlanInputs() only falls back to a live GPS
        // re-fetch when no coordinate was captured at all.
        originWasGps: startPlace?.source === 'gps',
        destination: document.getElementById('destination')?.value || '',
        destLat: typeof destPlace?.lat === 'number' ? destPlace.lat : null,
        destLng: typeof destPlace?.lng === 'number' ? destPlace.lng : null,
        departureDate: document.getElementById('departureDate')?.value || '',
        departureTime: document.getElementById('departureTime')?.value || '',
        rvType: document.getElementById('rvType')?.value || '',
        height: document.getElementById('height')?.value || '',
        weight: document.getElementById('weight')?.value || '',
        length: document.getElementById('length')?.value || ''
    };
};

// Applies a saved/pending route's inputs back onto the plan form. Shared
// by restorePendingRoute() (sign-in flow, localStorage) and
// window.loadSavedRoute() (DynamoDB-backed saved routes).
window.applyRoutePlanInputs = function(routeData) {
    let restored = false;

    if (routeData.origin) {
        const el = document.getElementById('start');
        if (el) {
            el.value = routeData.origin;
            restored = true;
            if (typeof routeData.originLat === 'number' && typeof routeData.originLng === 'number') {
                // Pin the exact point captured at save time -- skips both
                // free-text re-geocoding (can land on a coarse centroid for
                // vague text) and a live GPS re-fetch (would silently use
                // wherever THIS device/session currently is, not the saved
                // route's actual origin -- confirmed on the emulator, which
                // has no real GPS and substituted its default mock location).
                if (window.autocompleteService) {
                    window.autocompleteService.resolvedPlaces.start = {
                        lat: routeData.originLat,
                        lng: routeData.originLng,
                        formattedAddress: routeData.origin,
                        source: 'saved-route'
                    };
                }
            } else if (routeData.originWasGps) {
                // No coordinate was captured (route saved before originLat/
                // Lng existed) -- only remaining option is a fresh GPS fix.
                el.dataset.needsGpsRefresh = 'true';
            } else if (window.autocompleteService) {
                // No coordinate either way -- clear any stale resolved place
                // left over from earlier in this session so RouteService
                // falls through to geocoding the restored text itself,
                // rather than reusing an unrelated prior coordinate.
                window.autocompleteService.resolvedPlaces.start = null;
            }
        }
    }
    if (routeData.destination) {
        const el = document.getElementById('destination');
        if (el) {
            el.value = routeData.destination;
            restored = true;
            if (typeof routeData.destLat === 'number' && typeof routeData.destLng === 'number' && window.autocompleteService) {
                window.autocompleteService.resolvedPlaces.destination = {
                    lat: routeData.destLat,
                    lng: routeData.destLng,
                    formattedAddress: routeData.destination,
                    source: 'saved-route'
                };
            } else if (window.autocompleteService) {
                window.autocompleteService.resolvedPlaces.destination = null;
            }
        }
    }
    if (routeData.departureDate) {
        const el = document.getElementById('departureDate');
        if (el) el.value = routeData.departureDate;
    }
    if (routeData.departureTime) {
        const el = document.getElementById('departureTime');
        if (el) el.value = routeData.departureTime;
    }
    if (routeData.rvType) {
        const el = document.getElementById('rvType');
        if (el) el.value = routeData.rvType;
    }
    if (routeData.height) {
        const el = document.getElementById('height');
        if (el) el.value = routeData.height;
    }
    if (routeData.weight) {
        const el = document.getElementById('weight');
        if (el) el.value = routeData.weight;
    }
    if (routeData.length) {
        const el = document.getElementById('length');
        if (el) el.value = routeData.length;
    }

    return restored;
};

window.saveRouteForAfterSignIn = function() {
    const routeData = window.getRoutePlanInputs();

    // Only save if user has entered something meaningful
    if (routeData.origin || routeData.destination) {
        localStorage.setItem('zephyr_pending_route', JSON.stringify(routeData));
        console.log('📋 Route saved for after sign-in:', routeData.origin, '→', routeData.destination);
    }
};

window.restorePendingRoute = function() {
    const pending = localStorage.getItem('zephyr_pending_route');
    if (!pending) return false;

    try {
        const routeData = JSON.parse(pending);
        localStorage.removeItem('zephyr_pending_route');

        const restored = window.applyRoutePlanInputs(routeData);

        if (restored) {
            console.log('📋 Pending route restored:', routeData.origin, '→', routeData.destination);
            window.showToast('Your route was saved — ready to calculate!');
        }

        return restored;
    } catch (e) {
        console.error('📋 Error restoring pending route:', e);
        localStorage.removeItem('zephyr_pending_route');
        return false;
    }
};

// ═══════════════════════════════════════════════════════════════
// Saved Routes: Desktop-plan, mobile-load trip templates.
// Stores plan-form inputs (not the computed route) to DynamoDB via
// SavedRoutesService, so loading one later recomputes against current
// wind/elevation/road-quality data. See SavedRoutesService.js.
// ═══════════════════════════════════════════════════════════════

window.saveCurrentRouteAs = async function() {
    if (!window.isSubscribed) {
        if (!window.authService.isSignedIn()) { window.authService.signIn(); return; }
        if (!canPurchaseInApp()) { window.showToast?.('Saved Routes is a Pro feature.'); return; }
        window.subscriptionService.startCheckout(window.authService.getUser()?.sub, window.authService.getUser()?.email);
        return;
    }

    const inputs = window.getRoutePlanInputs();
    if (!inputs.origin || !inputs.destination) {
        alert('Enter an origin and destination first.');
        return;
    }

    const name = prompt('Name this route (e.g. "Denver to Moab"):');
    if (!name || !name.trim()) return;

    const result = await savedRoutesService.add(name.trim(), inputs);
    if (result) {
        if (window.showToast) window.showToast('Route saved!');
    } else {
        alert('Save failed — check if signed in');
    }
};

window.loadSavedRoute = function(id) {
    const route = savedRoutesService.getAll().find(r => r.id === id);
    if (!route) return false;

    const restored = window.applyRoutePlanInputs(route);
    if (restored) {
        console.log('📍 Saved route loaded:', route.name);
        setTimeout(() => {
            const calculateBtn = document.getElementById('calculateRoute');
            if (calculateBtn) calculateBtn.click();
        }, 300);
    }
    return restored;
};

window.showToast = function(message, opts = {}) {
    const existing = document.getElementById('zephyrToast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'zephyrToast';
    toast.textContent = message;
    // 'warning' (used for vehicle restriction alerts) is red and stays up
    // longer than a routine toast -- this is a safety-relevant message the
    // driver shouldn't miss while it flashes by in 4 seconds.
    const isWarning = opts.type === 'warning';
    const bg = isWarning ? '#c62828' : '#2c3e50';
    const duration = opts.duration || (isWarning ? 8000 : 4000);
    toast.style.cssText = `position: fixed; bottom: 2rem; left: 50%; transform: translateX(-50%); background: ${bg}; color: white; padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 0.9rem; z-index: 99999; box-shadow: 0 4px 12px rgba(0,0,0,0.3); transition: opacity 0.3s; max-width: 90vw; text-align: center;`;
    document.body.appendChild(toast);

    setTimeout(() => {
        toast.style.opacity = '0';
        setTimeout(() => toast.remove(), 300);
    }, duration);
};

// Phase 5.6: Facebook Login
const FB_APP_ID = '789872024057743';
const FB_REDIRECT_URI = 'https://zephyr.travel/app.html';
const API_URL_FB = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

window.facebookLogin = function() {
    const scope = 'email,public_profile';
    const fbAuthUrl = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${FB_APP_ID}&redirect_uri=${encodeURIComponent(FB_REDIRECT_URI)}&scope=${scope}&response_type=code`;
    window.location.href = fbAuthUrl;
};

// Phase 7: Trip Feedback — store pending trip on launch, show modal on return
window.storePendingTripFeedback = function(routeInfo) {
    localStorage.setItem('zephyr_pending_feedback', JSON.stringify({
        launchedAt: Date.now(),
        start: routeInfo.start || '',
        destination: routeInfo.destination || '',
        routeSegmentId: routeInfo.routeSegmentId || null,
        fuelStops: routeInfo.fuelStops || []
    }));
};

window.checkPendingTripFeedback = function() {
    // Check backend flag first (reliable across devices/sessions)
    const status = window.routeTrackingService?.launchStatus;
    if (status?.feedbackPending) {
        console.log('📋 Backend says feedback pending — showing modal');
        const modal = document.getElementById('tripFeedbackModal');
        if (modal) modal.style.display = 'flex';
        return;
    }

    // Fallback: check localStorage (for same-session feedback)
    const pending = localStorage.getItem('zephyr_pending_feedback');
    if (!pending) {
        console.log('📋 No pending trip feedback');
        return;
    }

    try {
        const data = JSON.parse(pending);
        const hoursSinceLaunch = (Date.now() - data.launchedAt) / (1000 * 60 * 60);
        console.log(`📋 Pending trip feedback: launched ${hoursSinceLaunch.toFixed(1)} hours ago`);

        // Show feedback modal if 24+ hours have passed
        if (hoursSinceLaunch >= 24) {
            console.log('📋 Showing trip feedback modal');
            const modal = document.getElementById('tripFeedbackModal');
            if (modal) {
                const fuelStopsContainer = document.getElementById('fb-fuel-stops');
                if (fuelStopsContainer && data.fuelStops && data.fuelStops.length > 0) {
                    fuelStopsContainer.style.display = 'block';
                    fuelStopsContainer.innerHTML = `
                        <label style="display: block; font-weight: 600; color: #2c3e50; margin-bottom: 0.5rem; font-size: 0.9rem;">Verify your fuel stops:</label>
                        ${data.fuelStops.map((stop, i) => `
                            <div style="padding: 0.5rem; background: #f8f9fa; border-radius: 6px; margin-bottom: 0.5rem; font-size: 0.85rem;">
                                <strong>⛽ ${stop.name}</strong>
                                <div style="display: flex; gap: 0.5rem; margin-top: 0.4rem; flex-wrap: wrap;">
                                    <label style="font-size: 0.8rem;"><input type="checkbox" class="fb-stop-open" data-index="${i}" checked> Open</label>
                                    <label style="font-size: 0.8rem;"><input type="checkbox" class="fb-stop-pullthrough" data-index="${i}"> Pull-through</label>
                                    <label style="font-size: 0.8rem;"><input type="checkbox" class="fb-stop-diesel" data-index="${i}" checked> Diesel</label>
                                </div>
                            </div>
                        `).join('')}
                    `;
                }
                modal.style.display = 'flex';
            } else {
                console.warn('📋 Trip feedback modal element not found');
            }
        }
    } catch (e) {
        console.error('📋 Error checking pending feedback:', e);
        localStorage.removeItem('zephyr_pending_feedback');
    }
};

window.submitTripFeedback = async function() {
    const wind = document.getElementById('fb-wind').value;
    const elevation = document.getElementById('fb-elevation').value;
    const fuel = document.getElementById('fb-fuel').value;
    if (!wind && !elevation && !fuel) {
        alert('Please answer at least one question.');
        return;
    }

    const user = window.authService?.getUser();
    if (!user) return;

    try {
        const pending = JSON.parse(localStorage.getItem('zephyr_pending_feedback') || '{}');

        // Submit trip feedback
        await authFetch(`${API_URL_FB}/api/feedback/trip`, {
            method: 'POST',
            body: JSON.stringify({
                userId: user.sub,
                windAccuracy: wind || null,
                elevationAccuracy: elevation || null,
                fuelStopAccurate: fuel === 'true' ? true : fuel === 'false' ? false : null,
                routeSegmentId: pending.routeSegmentId || null
            })
        });

        // Submit fuel stop verifications if any
        if (pending.fuelStops && pending.fuelStops.length > 0) {
            for (let i = 0; i < pending.fuelStops.length; i++) {
                const stop = pending.fuelStops[i];
                const openEl = document.querySelector(`.fb-stop-open[data-index="${i}"]`);
                const pullEl = document.querySelector(`.fb-stop-pullthrough[data-index="${i}"]`);
                const dieselEl = document.querySelector(`.fb-stop-diesel[data-index="${i}"]`);

                if (openEl) {
                    await authFetch(`${API_URL_FB}/api/fuel-stops/verify`, {
                        method: 'POST',
                        body: JSON.stringify({
                            userId: user.sub,
                            fuelStopId: stop.id,
                            isOpen: openEl.checked,
                            hasPullThrough: pullEl?.checked || false,
                            hasDiesel: dieselEl?.checked || false
                        })
                    });
                }
            }
        }

        localStorage.removeItem('zephyr_pending_feedback');
        document.getElementById('tripFeedbackModal').style.display = 'none';
        alert('Thanks for your feedback! 🙏');
    } catch (err) {
        console.error('Feedback submit error:', err);
        alert('Failed to submit feedback. Try again later.');
    }
};

window.dismissTripFeedback = function() {
    localStorage.removeItem('zephyr_pending_feedback');
    document.getElementById('tripFeedbackModal').style.display = 'none';
};

// Route mode toggle — RV (truck dimensions) vs Car (standard routing)
window.routeMode = "rv";
window.setRouteMode = function(mode) {
 window.routeMode = mode;
 const rvBtn = document.getElementById("rvModeBtn");
 const carBtn = document.getElementById("carModeBtn");
 const clearanceCheckbox = document.getElementById("avoidLowClearance");
 if (mode === "rv") {
 if (rvBtn) { rvBtn.style.background = "#1e88e5"; rvBtn.style.color = "white"; }
 if (carBtn) { carBtn.style.background = "transparent"; carBtn.style.color = "#666"; }
 if (clearanceCheckbox) clearanceCheckbox.checked = true;
 } else {
 if (carBtn) { carBtn.style.background = "#1e88e5"; carBtn.style.color = "white"; }
 if (rvBtn) { rvBtn.style.background = "transparent"; rvBtn.style.color = "#666"; }
 if (clearanceCheckbox) clearanceCheckbox.checked = false;
 }
};

// Silently prime window._lastGpsPosition on app load so AWS Location's
// Suggest API (autocomplete) has a BiasPosition available for the user's
// very first search -- without this, a cold search (before the user ever
// taps the GPS button or resolves a start point) has no lat/lng to send,
// and AWS's Suggest endpoint hard-requires one of BiasPosition/
// Filter.BoundingBox/Filter.Circle (a ValidationException otherwise, which
// surfaced as a silent "nothing pops up" search bug). This never touches
// the start input, resolvedPlaces['start'], or shows any UI -- it only
// exists to make autocomplete proximity-biased sooner. The Lambda also
// carries its own continental-US bounding-box fallback for when this
// hasn't resolved yet (denied permission, slow fix, etc.), so a search
// never hard-fails either way.
function primeLocationBiasInBackground() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
        (position) => {
            window._lastGpsPosition = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };
        },
        () => { /* denied/unavailable -- Lambda's bounding-box fallback covers this */ },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
}

/**
 * Does the actual GPS fetch + reverse-geocode + resolvedPlaces population for
 * the "start" field. Pulled out of useMyLocation() so handleCalculateRoute()
 * can also call it directly -- when a route was saved before sign-in and
 * restored afterward (see saveRouteForAfterSignIn/restorePendingRoute
 * below), the #start field's TEXT survives, but resolvedPlaces['start']
 * (kept only in JS memory) does not, and neither does any GPS fix taken
 * hours/days earlier remain a valid "current location." Silently falling
 * back to geocoding that stale text is what caused routes to start from
 * wherever GPS happened to be resolved last (confirmed recurring at Idaho
 * Springs) instead of where the user actually is now. Throws on failure —
 * callers decide how to surface that.
 */
async function resolveCurrentLocationIntoStart() {
    if (!navigator.geolocation) {
        throw new Error('Location is not available on this device.');
    }

    const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
        });
    });

    const { latitude, longitude } = position.coords;
    const startInput = document.getElementById('start');

    // Reverse geocode using OpenStreetMap Nominatim (free, no API key)
    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=18&addressdetails=1`);
        const data = await res.json();
        if (data.address) {
            const city = data.address.city || data.address.town || data.address.village || data.address.county || '';
            const state = data.address.state || '';
            if (city && state) {
                startInput.value = `${city}, ${state}`;
            } else if (city) {
                startInput.value = city;
            } else {
                startInput.value = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
            }
        } else {
            startInput.value = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        }
    } catch (e) {
        // Reverse geocode failed — use raw coordinates
        startInput.value = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }

    // Don't dispatch input event — it triggers autocomplete which overwrites GPS result
    // Set precise GPS coordinates — route calculation uses these directly
    if (window.autocompleteService) {
        window.autocompleteService.resolvedPlaces["start"] = {
            lat: latitude,
            lng: longitude,
            formattedAddress: startInput.value,
            source: "gps"
        };
        window._lastGpsPosition = { lat: latitude, lng: longitude };
    }
    delete startInput.dataset.needsGpsRefresh;
}
window.resolveCurrentLocationIntoStart = resolveCurrentLocationIntoStart;

window.useMyLocation = async function() {
    const btn = document.getElementById('gpsBtn');

    // Show loading state
    btn.textContent = '⏳';
    btn.disabled = true;

    try {
        await resolveCurrentLocationIntoStart();
    } catch (error) {
        if (error.code === 1) {
            alert('Location permission denied. Please allow location access in your device settings.');
        } else if (error.code === 2) {
            alert('Unable to determine your location. Please try again.');
        } else {
            alert('Location request timed out. Please try again.');
        }
    } finally {
        btn.textContent = '📍';
        btn.disabled = false;
    }
};

// Mobile: Toggle sidebar/map fullscreen after route calculation
window.toggleMobileSidebar = function() {
    const sidebar = document.querySelector('.sidebar');
    const container = document.querySelector('.container');
    const btn = document.getElementById('mobileShowSidebar');
    const mapTab = document.getElementById('viewMapTab');

    if (sidebar.classList.contains('mobile-hidden')) {
        // Show sidebar, shrink map
        sidebar.classList.remove('mobile-hidden');
        container.classList.remove('map-fullscreen');
        if (btn) {
            btn.style.display = 'block';
            btn.textContent = '🗺️ Show Map';
            btn.style.bottom = 'auto';
            btn.style.top = '4rem';
            btn.style.left = '50%';
            btn.style.transform = 'translateX(-50%)';
        }

        // Tab says MAP since sidebar is visible
        if (mapTab) { mapTab.style.display = 'block'; mapTab.innerHTML = '🗺️ MAP'; }

        // Phase 15: Hide the floating card when sidebar is visible
        if (window.hideRouteCard) window.hideRouteCard();
    } else {
        // Hide sidebar, fullscreen map
        sidebar.classList.add('mobile-hidden');
        container.classList.add('map-fullscreen');
        if (btn) {
            btn.textContent = '← Route Planning';
            btn.style.bottom = 'auto';
            btn.style.top = 'calc(4.5rem + env(safe-area-inset-top, 0px))';
            btn.style.left = '1rem';
            btn.style.transform = 'none';
            // Phase 15: Hide standalone button — card has Route Planning built in
            btn.style.display = 'none';
        }

        // Tab says PLAN since map is visible
        if (mapTab) { mapTab.style.display = 'block'; mapTab.innerHTML = '📋 PLAN'; }
        // Trigger map resize so MapLibre recalculates its size
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            if (window.uiController && window.uiController.mapService && window.uiController.mapService.map) {
                window.uiController.mapService.map.resize();
                // Fit bounds to the current route
                if (window.uiController.currentRoutes && window.uiController.currentRoutes.length > 0) {
                    const routeIndex = window.uiController.selectedRouteIndex !== undefined 
                        ? window.uiController.selectedRouteIndex 
                        : window.uiController.bestRouteIndex;
                    const route = window.uiController.currentRoutes[routeIndex];
                    if (route && route.coordinates) {
                        window.uiController.mapService.fitRouteToView(route.coordinates);
                    }
                }
            }
        }, 150);

        // Phase 15: Show the floating card when map is fullscreen
        if (window.showRouteCard) window.showRouteCard();
    }
};

// Show the toggle button on mobile after route is calculated
window.showMobileMapToggle = function() {
    if (window.innerWidth <= 1024) {
        const sidebar = document.querySelector('.sidebar');
        const container = document.querySelector('.container');
        const btn = document.getElementById('mobileShowSidebar');
        sidebar.classList.add('mobile-hidden');
        container.classList.add('map-fullscreen');
        if (btn) {
            btn.style.display = 'none';
        }
        // Show the right-edge tab as PLAN (map is now visible)
        const mapTab = document.getElementById('viewMapTab');
        if (mapTab) { mapTab.style.display = 'block'; mapTab.innerHTML = '📋 PLAN'; }
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            if (window.uiController && window.uiController.mapService && window.uiController.mapService.map) {
                window.uiController.mapService.map.resize();
                // Fit bounds to the calculated route
                if (window.uiController.currentRoutes && window.uiController.currentRoutes.length > 0) {
                    const routeIndex = window.uiController.selectedRouteIndex !== undefined 
                        ? window.uiController.selectedRouteIndex 
                        : window.uiController.bestRouteIndex;
                    const route = window.uiController.currentRoutes[routeIndex];
                    if (route && route.coordinates) {
                        window.uiController.mapService.fitRouteToView(route.coordinates);
                    }
                }
            }
        }, 300);

        // Phase 15: Show the floating route card on the map
        if (window.showRouteCard) window.showRouteCard();
    }
};

// ═══════════════════════════════════════════════════════════════
// Phase 15: Collapsible Route Card Overlay
// Floating card on map — launch button always one tap away.
// ═══════════════════════════════════════════════════════════════

/**
 * Show the route card overlay on the map.
 * Called after route calculation when map goes fullscreen.
 */
window.showRouteCard = function() {
    const overlay = document.getElementById('routeCardOverlay');
    if (!overlay) return;

    overlay.classList.remove('hidden');

    // Populate card with current route data
    window.updateRouteCard();
    window.updateCardLaunchButton();
    // Phase 27: sync the departure time slider to the plan form's actual time
    if (window.initDepartureSlider) window.initDepartureSlider();

    // Start in collapsed state
    window.collapseRouteCard();
};

/**
 * Hide the route card overlay (when returning to sidebar view).
 */
window.hideRouteCard = function() {
    const overlay = document.getElementById('routeCardOverlay');
    if (overlay) overlay.classList.add('hidden');
};

/**
 * Expand the card to show full route details.
 */
window.expandRouteCard = function() {
    const collapsed = document.getElementById('routeCardCollapsed');
    const expanded = document.getElementById('routeCardExpanded');
    if (collapsed) collapsed.style.display = 'none';
    if (expanded) expanded.classList.remove('hidden');
};

/**
 * Collapse the card back to summary + launch button.
 */
window.collapseRouteCard = function() {
    const collapsed = document.getElementById('routeCardCollapsed');
    const expanded = document.getElementById('routeCardExpanded');
    if (collapsed) collapsed.style.display = 'block';
    if (expanded) expanded.classList.add('hidden');
};

/**
 * Populate the card with route summary data from the current route.
 */
// Checklist on the route card while a new route's details load
// (UIController.showRoutePreview starts it, then ticks each step off).
// Each step: 'pending' | 'done' | 'failed' | 'skip' (not asked for).
window.routeProgress = null;
const ROUTE_PROGRESS_LABELS = {
    wind: ['Wind along your route…', 'Wind checked', 'Wind unavailable'],
    elevation: ['Hills and grades…', 'Hills and grades checked', 'Hills and grades unavailable'],
    clearances: ['Low clearances…', 'Clearances checked', "Couldn't check clearances"],
    fuel: ['Fuel stops…', 'Fuel stops found', 'Fuel stops unavailable']
};
let routeProgressHideTimer = null;

function renderRouteProgress() {
    const progress = window.routeProgress;
    const html = !progress ? '' : Object.entries(progress)
        .filter(([, state]) => state !== 'skip')
        .map(([step, state]) => {
            const [pending, done, failed] = ROUTE_PROGRESS_LABELS[step];
            if (state === 'pending') return `<div class="route-progress-step">⏳ ${w(pending)}</div>`;
            if (state === 'done') return `<div class="route-progress-step done">✓ ${w(done)}</div>`;
            return `<div class="route-progress-step failed">⚠️ ${w(failed)}</div>`;
        }).join('');
    ['cardProgress', 'cardProgressExpanded'].forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.innerHTML = html;
        el.style.display = html ? 'block' : 'none';
    });
}

window.startRouteProgress = function(steps) {
    clearTimeout(routeProgressHideTimer);
    window.routeProgress = { ...steps };
    renderRouteProgress();
};

window.setRouteProgress = function(step, state) {
    const progress = window.routeProgress;
    if (!progress || !(step in progress)) return;
    progress[step] = state;
    renderRouteProgress();
    // Everything answered: leave the ticks up briefly, then tidy the card.
    if (Object.values(progress).every((s) => s !== 'pending')) {
        clearTimeout(routeProgressHideTimer);
        routeProgressHideTimer = setTimeout(() => {
            if (window.routeProgress === progress) { window.routeProgress = null; renderRouteProgress(); }
        }, 3000);
    }
};

window.clearRouteProgress = function() {
    clearTimeout(routeProgressHideTimer);
    window.routeProgress = null;
    renderRouteProgress();
};

window.updateRouteCard = function() {
    const ui = window.uiController;
    if (!ui || !ui.currentRoutes || ui.currentRoutes.length === 0) return;

    const routeIndex = ui.selectedRouteIndex !== undefined
        ? ui.selectedRouteIndex
        : ui.bestRouteIndex;
    const route = ui.currentRoutes[routeIndex];
    if (!route) return;

    // Calculate total trip time including stop durations
    const stops = ui.getRouteStops(routeIndex);
    const stopCount = stops ? stops.length : 0;
    const prefs = window.travelPreferences || {};
    const stopMinutes = stopCount * (prefs.fuelStopDurationMinutes || 30);
    const drivingHours = route.durationValue || 0; // raw driving hours
    const totalMinutes = Math.round(drivingHours * 60) + stopMinutes;
    let totalHours = Math.floor(totalMinutes / 60);
    let remainingMinutes = totalMinutes % 60;

    const totalDuration = remainingMinutes > 0
        ? `${totalHours} hrs ${remainingMinutes} min`
        : `${totalHours} hrs`;

    // Collapsed state — distance and total duration (with stops)
    const distEl = document.getElementById('cardDistance');
    const durEl = document.getElementById('cardDuration');
    if (distEl) distEl.textContent = units.routeDistanceText(route) || '';
    if (durEl) durEl.textContent = totalDuration;

    // Expanded state — full details
    const detailsEl = document.getElementById('cardDetails');
    if (!detailsEl) return;

    let html = '';

    // Distance & Duration
    html += `
        <div class="route-card-detail-row">
            <span class="route-card-detail-label">Distance</span>
            <span class="route-card-detail-value">${units.routeDistanceText(route) || '—'}</span>
        </div>
        <div class="route-card-detail-row">
            <span class="route-card-detail-label">Drive Time</span>
            <span class="route-card-detail-value">${route.duration || '—'}</span>
        </div>
    `;

    // Show total with stops if stops exist
    if (stopCount > 0) {
        html += `
            <div class="route-card-detail-row">
                <span class="route-card-detail-label">Total w/ Stops</span>
                <span class="route-card-detail-value">${totalDuration} (${stopCount} × ${prefs.fuelStopDurationMinutes || 30} min)</span>
            </div>
        `;
    }

    // Wind conditions
    if (route.windAnalysis) {
        const avgWind = isNaN(route.windAnalysis.avgWindSpeed) ? 0 : route.windAnalysis.avgWindSpeed;
        let windClass = 'low';
        let windText = 'Low';
        if (avgWind > 20) {
            windClass = 'high';
            windText = 'High';
        } else if (avgWind > 15) {
            windClass = 'moderate';
            windText = 'Moderate';
        }
        html += `
            <div class="route-card-detail-row">
                <span class="route-card-detail-label">🌬️ Wind</span>
                <span class="route-card-detail-value">
                    Avg ${Math.ceil(units.speed(avgWind))} ${units.unit.speed()}
                    <span class="route-card-wind-badge ${windClass}">${windText}</span>
                </span>
            </div>
        `;
        if (route.windAnalysis.warnings && route.windAnalysis.warnings.length > 0) {
            html += `
                <div class="route-card-detail-row">
                    <span class="route-card-detail-label"></span>
                    <span class="route-card-detail-value" style="color: #e65100; font-size: 0.8rem;">
                        ⚠️ ${route.windAnalysis.warnings.length} wind alert(s)
                    </span>
                </div>
            `;
        }
    }

    // Elevation
    if (route.elevationAnalysis) {
        const stats = route.elevationAnalysis.stats;
        const routeMiles = route.distanceValue || 0;

        // If maxGrade is 0 on a route longer than 20 miles, data is unreliable
        if (stats.maxGrade === 0 && routeMiles > 20) {
            html += `
                <div class="route-card-detail-row">
                    <span class="route-card-detail-label">⛰️ Elevation</span>
                    <span class="route-card-detail-value" style="color: #90a4ae;">Not Available</span>
                </div>
            `;
        } else {
            let elevClass = 'easy';
            let elevText = 'Easy';
            const totalChange = stats.totalGain + stats.totalLoss;
            if (stats.steepGradeCount > 5 || stats.maxGrade > 6 || totalChange > 5000) {
                elevClass = 'challenging';
                elevText = 'Challenging';
            } else if (stats.steepGradeCount > 0 || stats.maxGrade > 4 || totalChange > 2000) {
                elevClass = 'moderate';
                elevText = 'Moderate';
            }
            html += `
                <div class="route-card-detail-row">
                    <span class="route-card-detail-label">⛰️ Elevation</span>
                    <span class="route-card-detail-value">
                        Max ${stats.maxGrade}% grade
                        <span class="route-card-elevation-badge ${elevClass}">${elevText}</span>
                    </span>
                </div>
            `;
            if (stats.steepGradeCount > 0) {
                html += `
                    <div class="route-card-detail-row">
                        <span class="route-card-detail-label"></span>
                        <span class="route-card-detail-value" style="color: #e65100; font-size: 0.8rem;">
                            ⚠️ ${stats.steepGradeCount} steep section(s) over 4%
                        </span>
                    </div>
                `;
            }
        }
    }

    // Fuel stops count (reuse stops from duration calculation above)
    if (stops && stops.length > 0) {
        html += `
            <div class="route-card-detail-row">
                <span class="route-card-detail-label">⛽ Fuel Stops</span>
                <span class="route-card-detail-value">${stops.length} stop${stops.length > 1 ? 's' : ''} added</span>
            </div>
        `;
    }

    // Road quality (Phase 18)
    const roadQuality = ui.currentRoadQuality;
    if (roadQuality && roadQuality.summary && roadQuality.summary.overall !== 'unknown') {
        const rq = roadQuality.summary;
        const roughCount = rq.roughSegments || 0;
        const poorCount = rq.poorSegments || 0;
        const fairCount = rq.fairSegments || 0;

        // Badge logic: override if rough/fair spots exist
        let rqClass = 'low';
        let rqText = 'Smooth';
        if (rq.overall === 'poor' || poorCount >= 5) { rqClass = 'high'; rqText = 'Rough'; }
        else if (rq.overall === 'fair' || poorCount > 0 || fairCount > 5) { rqClass = 'moderate'; rqText = 'Fair'; }
        else if (rq.overall === 'good') { rqClass = 'low'; rqText = 'Good'; }
        else if (rq.overall === 'excellent') { rqClass = 'low'; rqText = 'Smooth'; }

        // Build descriptive count: "3 rough, 16 fair spots"
        let countText = '';
        if (roughCount > 0) {
            const parts = [];
            if (poorCount > 0) parts.push(`${poorCount} rough`);
            if (fairCount > 0) parts.push(`${fairCount} fair`);
            if (parts.length > 0) {
                countText = parts.join(', ') + ' spot' + (roughCount !== 1 ? 's' : '');
            } else {
                countText = `${roughCount} spot${roughCount !== 1 ? 's' : ''}`;
            }
        }

        html += `
            <div class="route-card-detail-row">
                <span class="route-card-detail-label">🛣️ Road</span>
                <span class="route-card-detail-value">
                    <span class="route-card-wind-badge ${rqClass}">${rqText}</span>
                    ${countText}
                </span>
            </div>
        `;

        // Pro: collapsible segment list in map card (rough only, merged ranges)
        if (roadQuality.segments && roadQuality.segments.length > 0) {
            const routeIdx = ui.selectedRouteIndex !== undefined ? ui.selectedRouteIndex : ui.bestRouteIndex;
            const rCoords = ui.currentRoutes?.[routeIdx]?.coordinates || [];
            const filtered = roadQuality.segments
                .filter(s => {
                    if (s.iriRating !== 'poor') return false;
                    if (!s.highway?.startsWith('I-') && !s.highway?.startsWith('US-')) return false;
                    return isSegmentOnRoute(s, rCoords);
                })
                .sort((a, b) => {
                    if (a.highway !== b.highway) return a.highway.localeCompare(b.highway);
                    return (a.beginMile || 0) - (b.beginMile || 0);
                });
            const merged = [];
            for (const seg of filtered) {
                const mile = Math.round(seg.beginMile || 0);
                const last = merged[merged.length - 1];
                if (last && last.highway === seg.highway && mile - last.endMile <= 10) {
                    last.endMile = Math.max(last.endMile, Math.round(seg.endMile || mile));
                } else {
                    merged.push({ highway: seg.highway, startMile: mile, endMile: Math.round(seg.endMile || mile) });
                }
            }
            merged.sort((a, b) => a.startMile - b.startMile);
            const notable = merged.slice(0, 10);
            if (notable.length > 0) {
                html += `<div class="route-card-detail-row"><span class="route-card-detail-label"></span><span class="route-card-detail-value"><details style="font-size: 0.8rem;"><summary style="cursor:pointer; color:#546e7a; font-weight:600;">Rough spots (${notable.length})</summary>`;
                notable.forEach(r => {
                    const m = r.startMile === r.endMile ? `Mile ${r.startMile}` : `Mile ${r.startMile}–${r.endMile}`;
                    html += `<div style="display:flex;justify-content:space-between;padding:0.2rem 0;">${r.highway} · ${m} <span style="color:#ef4444;font-weight:600;">Rough</span></div>`;
                });
                html += `</details></span></div>`;
            }
        }
    }

    detailsEl.innerHTML = html;
};

/**
 * Dynamically import NavigationService.js, self-healing from a specific
 * failure mode confirmed on a real device: a network blip mid-route made
 * the import() fail ONCE, and every later "Navigate" tap in that same app
 * session failed the identical way afterward — even once signal came
 * back — until the app was closed and reopened. That's expected per the
 * HTML spec, not a fluke: a module's fetch result (success OR failure) is
 * cached in the page's module map keyed by resolved URL, permanently, for
 * that page's lifetime. Calling import() again with the same specifier
 * just replays the cached rejection; it never re-fetches.
 *
 * The only way to force a genuinely fresh fetch is to request a URL that's
 * actually different as a string. Chromium's rejection message happens to
 * embed the exact resolved chunk URL ("Failed to fetch dynamically
 * imported module: <url>") — not documented as a stable API, but reliable
 * here since this only ever runs in the Capacitor Android WebView
 * (Chromium-based). Parse that URL out and retry once with a cache-busting
 * query param, which the browser treats as a distinct module and actually
 * fetches over the network again.
 */
async function importNavigationServiceWithRetry() {
    try {
        return await import('./navigation/NavigationService.js');
    } catch (err) {
        const match = /Failed to fetch dynamically imported module: (\S+)/.exec(err?.message || '');
        if (!match) throw err; // Some other failure — nothing to retry against.
        console.warn('🧭 NavigationService import failed, retrying with a cache-busted URL:', err.message);
        const retryUrl = match[1] + (match[1].includes('?') ? '&' : '?') + 'retry=' + Date.now();
        // @vite-ignore — this is a fully-resolved runtime URL, not a source
        // path Vite needs to rewrite; the retry targets the exact same
        // built chunk the first attempt did, just with a cache-busting
        // query string appended.
        return await import(/* @vite-ignore */ retryUrl);
    }
}

/**
 * Phase 45 — user-controlled 3D route preview. Resolves the currently
 * selected route the exact same way startZephyrNavigation() does below, so
 * the preview always matches whatever route Navigate would actually launch.
 * Loads the route fit to view with full free camera control; Play/Pause
 * (below) drives the camera along the actual path from there.
 */
window.previewRouteIn3D = async function() {
    const uiCtrl = window.uiController;
    if (!uiCtrl || !uiCtrl.currentRoutes) {
        if (window.showToast) window.showToast('Calculate a route first');
        return;
    }

    const routeIndex = uiCtrl.selectedRouteIndex !== undefined ? uiCtrl.selectedRouteIndex : uiCtrl.bestRouteIndex;
    const route = uiCtrl.currentRoutes[routeIndex];
    if (!route || !route.coordinates || route.coordinates.length < 2) {
        if (window.showToast) window.showToast('No route to preview');
        return;
    }

    const mapService = uiCtrl.mapService;
    if (!mapService) return;

    // On mobile the map may be behind the sidebar — bring it forward the
    // same way the existing "view map" toggle does, so the preview is
    // actually visible instead of loading off-screen.
    if (window.innerWidth <= 1024 && window.showMobileMapToggle) {
        window.showMobileMapToggle();
    }

    // The floating route card (z-index 9999, "Navigate with Zephyr" etc.)
    // sits on top of nearly this whole overlay's control row on both
    // desktop (bottom-right) and mobile (full-width bottom bar) -- hide it
    // while previewing so Play/Close aren't fighting it for the same screen
    // space, same as the fullscreen-map toggle already does elsewhere.
    // showMobileMapToggle() above shows the card itself as part of going
    // fullscreen, then runs a delayed (300ms) resize/fit-bounds step -- hide
    // it now, and again just after that delay, in case anything in it
    // re-shows the card before this preview overlay is visible.
    if (window.hideRouteCard) window.hideRouteCard();
    setTimeout(() => { if (window.hideRouteCard) window.hideRouteCard(); }, 350);

    const overlay = document.getElementById('flythroughOverlay');
    const progressBar = document.getElementById('flythroughProgressBar');
    const playBtn = document.getElementById('flythroughPlayBtn');
    if (progressBar) progressBar.style.width = '0%';

    const loaded = mapService.loadRoutePreview(route, {
        onProgress: (fraction) => {
            if (progressBar) progressBar.style.width = `${Math.round(fraction * 100)}%`;
        },
        onPlayStateChange: (playing) => {
            if (playBtn) playBtn.textContent = playing ? '⏸ Pause' : '▶ Play';
        }
    });
    if (!loaded) {
        if (window.showToast) window.showToast('No route to preview');
        return;
    }

    if (playBtn) playBtn.textContent = '▶ Play';
    const speedBtn = document.getElementById('flythroughSpeedBtn');
    if (speedBtn) speedBtn.textContent = '1x';
    if (overlay) overlay.classList.remove('hidden');
};

/** Toggles Play/Pause on the currently loaded route preview. */
window.toggleRoutePreviewPlayback = function() {
    const mapService = window.uiController?.mapService;
    if (!mapService) return;
    if (mapService.isRoutePreviewPlaying()) {
        mapService.pauseRoutePreview();
    } else {
        mapService.playRoutePreview();
    }
};

/** Cycles the preview's playback speed (0.5x / 1x / 1.5x / 2x). */
window.cycleRoutePreviewSpeed = function() {
    const mapService = window.uiController?.mapService;
    if (!mapService) return;
    const SPEEDS = [0.5, 1, 1.5, 2, 3, 4, 5];
    const current = mapService.getPreviewSpeed();
    const next = SPEEDS[(SPEEDS.indexOf(current) + 1) % SPEEDS.length];
    mapService.setPreviewSpeed(next);
    const speedBtn = document.getElementById('flythroughSpeedBtn');
    if (speedBtn) speedBtn.textContent = `${next}x`;
};

window.cancelRouteFlythrough = function() {
    const uiCtrl = window.uiController;
    const mapService = uiCtrl?.mapService;
    mapService?.closeRoutePreview();
    const overlay = document.getElementById('flythroughOverlay');
    if (overlay) overlay.classList.add('hidden');
    if (window.showRouteCard) window.showRouteCard();

    // Closing the preview leaves the camera wherever playback stopped --
    // tilted, rotated, zoomed into one spot. Restore the original view: all
    // route alternatives drawn and the camera fit back to a flat overview,
    // the same way it looked right after routes were first calculated.
    // pitch/bearing are passed into the SAME fitBounds call (via fitOptions)
    // rather than a separate easeTo() afterward -- MapLibre only runs one
    // camera animation at a time, so a second call was cutting the first
    // one's zoom-out off before it finished.
    if (mapService && uiCtrl?.currentRoutes?.length > 0) {
        const selectedIndex = uiCtrl.selectedRouteIndex !== undefined ? uiCtrl.selectedRouteIndex : uiCtrl.bestRouteIndex;
        mapService.drawMultipleRoutes(
            uiCtrl.currentRoutes,
            selectedIndex,
            (index) => uiCtrl.selectRoute(index),
            { pitch: 0, bearing: 0, duration: 800 }
        );
    }
};

/**
 * Update the launch button state on the card.
 * Phase 19: Maps export is free — no counter, no paywall. Platform-aware labels.
 */
window.startZephyrNavigation = async function() {
    try {
        const { navigationService } = await importNavigationServiceWithRetry();

        if (!navigationService.isAvailable()) {
            if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
                if (window.showToast) window.showToast('Turn-by-turn navigation is coming to iPhone soon. For now it’s in the Zephyr Android app.', { duration: 6000 });
            } else if (confirm('Turn-by-turn navigation is in the Zephyr Android app. Open Google Play?')) {
                window.open('https://play.google.com/store/apps/details?id=travel.zephyr.app', '_blank', 'noopener');
            }
            return;
        }

        if (window.showToast) window.showToast('Preparing navigation...');

        const uiCtrl = window.uiController;
        if (!uiCtrl || !uiCtrl.currentRoutes) {
            if (window.showToast) window.showToast('Calculate a route first');
            return;
        }

        const routeIndex = uiCtrl.selectedRouteIndex !== undefined ? uiCtrl.selectedRouteIndex : uiCtrl.bestRouteIndex;
        const route = uiCtrl.currentRoutes[routeIndex];
        if (!route) {
            if (window.showToast) window.showToast('No route selected');
            return;
        }

        const userPlan = window.routeTrackingService?.launchStatus?.plan || (window.isSubscribed ? 'premium' : 'free');
        // window.RVProfileService is the RVProfileService *class* (only ever
        // has static methods — see main.js's own `window.RVProfileService =
        // RVProfileService` import), not an instance, and it has never had a
        // getActiveProfile method at all — this was silently sending {} for
        // every navigation session, initial route included, so every reroute
        // has been using the Lambda's generic DEFAULT_TRUCK fallback instead
        // of the user's real rig. Read the same live form fields the planning
        // screen's own "Calculate Route" flow uses, and run them through the
        // same buildRigProfile() transform so the field names actually match
        // what the Lambda expects (heightFt/widthFt/lengthFt/
        // grossVehicleWeightLbs, not height/width/length/weight).
        const navRvSpecs = {
            rvType: document.getElementById('rvType')?.value,
            height: parseFloat(document.getElementById('height')?.value),
            weight: parseFloat(document.getElementById('weight')?.value),
            length: parseFloat(document.getElementById('length')?.value)
        };
        const rigProfile = uiCtrl.routeService?.buildRigProfile?.(navRvSpecs) || {};

        // Get cached AWS response for full step data (not null)
        const cachedAWS = window.routeService?.getCachedRouteForNavigation?.(routeIndex);
        const awsRawResponse = cachedAWS?.route || null;

        // Grade warnings during nav: one point per steep stretch. Sending every
        // steep sample meant the first one spoke and the 15-minute limit hid
        // the rest, the worst included (drive 2026-11-02). Road quality: only
        // the poor-rated segments. Shared with the route-hazards Lambda
        // (routeHazards.js), which builds the same for Android Auto trips.
        const gradeData = gradeSegmentsFrom(route.elevationAnalysis);
        const roadQualityData = roadQualitySegmentsFrom(uiCtrl.currentRoadQuality);

        const result = await navigationService.startNavigation(
            route, awsRawResponse, rigProfile, userPlan,
            {
                windData: route.timeAwareWindProfile?.windProfile || [], gradeData, campgrounds: [],
                restrictions: (window._routeClearanceWarnings?.[routeIndex] || []).filter(w => w.lat && w.lng),
                roadQualityData,
                // Added stops: the app keeps the ones still ahead when it reroutes.
                // Through-cities (search-based, Pro) merge into the SAME list --
                // "force through this point, drop it once passed" is exactly
                // stops' existing native semantics, so no native code needed
                // for through-cities to also hold on reroute.
                stops: [
                    ...(uiCtrl.getRouteStops(routeIndex) || []).map(s => ({ lat: s.lat, lng: s.lng, name: s.name })),
                    ...(uiCtrl.throughCities || []).map(c => ({ lat: c.lat, lng: c.lng, name: c.label }))
                ],
                // Avoid cities: kept for every reroute during this nav session.
                avoidAreas: uiCtrl.avoidCities || []
            }
        );

        if (!result.started && result.error) {
            if (window.showToast) window.showToast('Nav error: ' + result.error);
        }

        if (result.started) {
            // Record the navigation start (count, log line, state map).
            // In the background: never delays or blocks navigation.
            const user = window.authService?.getUser?.();
            if (user?.sub && window.routeTrackingService) {
                import('./services/StateService.js')
                    .then(({ getStatesAlongRoute }) => getStatesAlongRoute(route.coordinates || []))
                    .catch(() => [])
                    .then((states) => window.routeTrackingService.launchRoute(user.sub, states, 'phone'))
                    .catch((err) => console.warn('Launch tracking failed (non-blocking):', err));
            }

            const { voicePromptEngine } = await import('./navigation/VoicePromptEngine.js');
            voicePromptEngine.init({ isPro: navigationService.isPro(userPlan) });
            navigationService.onNavigationEvent((event) => {
                // Native fires event fields flat on the object itself
                // (ZephyrNavigationPlugin.notifyEvent), not nested under .data.
                // event.isTest (set by ZephyrNavigationActivity.fireTestVoiceAlerts(),
                // the debug "Test Voice Alerts" menu button) routes through
                // forcePrompt() instead of evaluate() -- evaluate()'s 15-min
                // per-trigger-type cooldown and permanent per-segment dedup
                // made a repeat test press fire fine on the native side but
                // get silently swallowed here. forcePrompt() is the same
                // bypass VoicePromptEngine already offers for critical
                // real-world alerts, repurposed here for on-demand testing.
                if (event.type === 'gradeWarning') {
                    const data = {
                        gradePct: event.gradePct,
                        isDescending: event.isDescending,
                        distanceMiles: event.distanceMiles
                    };
                    if (event.isTest) voicePromptEngine.forcePrompt('grade', data);
                    else voicePromptEngine.evaluate('grade', data, event.segmentId);
                } else if (event.type === 'restrictionWarning') {
                    const data = {
                        restrictionType: event.restrictionType,
                        name: event.name,
                        clearanceFt: event.clearanceFt,
                        rigHeightFt: event.rigHeightFt,
                        // App 145+: the full sentence, worded by the app
                        // (distance, road, the half-mile reminder).
                        text: event.text
                    };
                    if (event.isTest) voicePromptEngine.forcePrompt('restriction', data);
                    else voicePromptEngine.evaluate('restriction', data, event.segmentId);
                } else if (event.type === 'roadQualityWarning') {
                    const data = {
                        rating: event.rating,
                        highway: event.highway
                    };
                    if (event.isTest) voicePromptEngine.forcePrompt('roadQuality', data);
                    else voicePromptEngine.evaluate('roadQuality', data, event.segmentId);
                } else if (event.type === 'windChange') {
                    const data = {
                        headwindMph: event.headwindMph,
                        gustMph: event.gustMph,
                        windDirection: event.windDirection,
                        lat: event.lat,
                        lng: event.lng
                    };
                    if (event.isTest) voicePromptEngine.forcePrompt('wind', data);
                    else voicePromptEngine.evaluate('wind', data);
                } else if (event.type === 'crosswindWarning') {
                    const data = {
                        crosswindMph: event.crosswindMph,
                        gustMph: event.gustMph,
                        side: event.side,
                        windDirection: event.windDirection
                    };
                    if (event.isTest) voicePromptEngine.forcePrompt('crosswind', data);
                    else voicePromptEngine.evaluate('crosswind', data, event.segmentId);
                } else if (event.type === 'muteStateChanged') {
                    // From ZephyrNavigationActivity's global mute button or
                    // its "Mute Alerts" menu toggle -- independent of
                    // turn-by-turn's own native mute, which never touches
                    // VoicePromptEngine at all.
                    voicePromptEngine.setMuted(!!event.alertsMuted);
                } else if (event.type === 'arrived' || event.type === 'stopped' || event.type === 'error') {
                    voicePromptEngine.endSession();
                }
            });
        }
    } catch (err) {
        // Catches failures at every stage above, including a failed dynamic
        // import of NavigationService.js — previously this threw an unhandled
        // promise rejection with zero visible feedback (silent no-op on tap).
        console.error('🧭 startZephyrNavigation failed:', err);
        if (window.showToast) window.showToast('Nav crash: ' + err.message);
    }
};

window.confirmLaunch = window.startZephyrNavigation;

window.updateCardLaunchButton = function() {
    const btnCollapsed = document.getElementById('cardLaunchBtn');
    const btnExpanded = document.getElementById('cardLaunchBtnExpanded');
    const textCollapsed = document.getElementById('cardLaunchText');
    const textExpanded = document.getElementById('cardLaunchTextExpanded');

    if (!btnCollapsed || !btnExpanded) return;

    // The route is on the map but wind/elevation are still being checked
    // (UIController.showRoutePreview): navigation needs those, so wait.
    if (window.routeAnalysisPending) {
        if (textCollapsed) textCollapsed.textContent = '⏳ Preparing route…';
        if (textExpanded) textExpanded.textContent = '⏳ Preparing route…';
        btnCollapsed.disabled = true;
        btnExpanded.disabled = true;
        btnCollapsed.onclick = function(e) { e.stopPropagation(); };
        btnExpanded.onclick = null;
        return;
    }
    btnCollapsed.disabled = false;
    btnExpanded.disabled = false;

    // Phase 40: All users navigate with Zephyr — no Google Maps handoff
    const primaryLabel = '🧭 Navigate with Zephyr';

    // Clean button — navigate with Zephyr for everyone
    if (textCollapsed) textCollapsed.textContent = primaryLabel;
    if (textExpanded) textExpanded.textContent = primaryLabel;
    btnCollapsed.className = 'route-card-launch-btn';
    btnExpanded.className = 'route-card-launch-btn route-card-launch-expanded';

    // Wire to Zephyr navigation
    btnCollapsed.onclick = function(e) { e.stopPropagation(); window.startZephyrNavigation(); };
    btnExpanded.onclick = function() { window.startZephyrNavigation(); };

    // Phase 40: Override the route detail panel button
    const detailBtn = document.getElementById('exportNavigation');
    if (detailBtn) {
        detailBtn.innerHTML = '🧭 Navigate with Zephyr';
        detailBtn.style.background = 'linear-gradient(135deg, #F5A623, #E8942A)';
        detailBtn.style.color = '#1a1a2e';
        detailBtn.style.borderColor = '#F5A623';
        detailBtn.onclick = function() { window.startZephyrNavigation(); };
    }
};

// Phase 10: Road Reporting
const API_URL_RR = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

// Saved Routes (Pro) -- unlock the desktop save button and the mobile
// "Saved Routes" menu entry, mirroring unlockCampgroundFilters below.
window.unlockSavedRoutes = function() {
    const btn = document.getElementById('saveRouteBtn');
    const badge = document.getElementById('saveRouteProBadge');
    const upgrade = document.getElementById('saveRouteUpgrade');
    if (btn) { btn.style.opacity = '1'; btn.style.pointerEvents = 'auto'; }
    if (badge) badge.style.display = 'none';
    if (upgrade) upgrade.style.display = 'none';

    const menuBadge = document.getElementById('savedRoutesMenuProBadge');
    if (menuBadge) menuBadge.style.display = 'none';
};

// Phase 27: Departure Time Slider (Pro) -- lets a user drag through the day
// to preview wind conditions at a different departure time without
// recalculating the whole route (see UIController.refreshWindForDepartureTime,
// which only re-fetches wind for the already-calculated route/waypoints).
// Two instances share this logic: the desktop panel inside #routeInfo
// (hidden on mobile) and the mobile route-card overlay's copy -- dragging
// either one keeps both in sync and only fires one wind refresh.
const DEPARTURE_SLIDER_IDS = [
    { slider: 'departureSlider', label: 'departureSliderLabel' },
    { slider: 'cardDepartureSlider', label: 'cardDepartureSliderLabel' }
];

// Avoid-a-City (Pro) -- same lock/unlock pattern as unlockSavedRoutes above.
// Gated client-side only, consistent with every other Pro feature in this
// app (campground filters, saved routes, departure slider): nothing server-
// side currently checks plan per-request on rv-route, so this is a UI gate,
// not a guarantee the preference can't reach the API another way.
window.unlockAvoidCities = function() {
    const controls = document.getElementById('avoidCityControls');
    const badge = document.getElementById('avoidCityProBadge');
    const upgrade = document.getElementById('avoidCityUpgrade');
    if (controls) { controls.style.opacity = '1'; controls.style.pointerEvents = 'auto'; }
    if (badge) badge.style.display = 'none';
    if (upgrade) upgrade.style.display = 'none';
};

// Route Through a City (Pro) -- same lock/unlock pattern as unlockAvoidCities above.
window.unlockThroughCities = function() {
    const controls = document.getElementById('throughCityControls');
    const badge = document.getElementById('throughCityProBadge');
    const upgrade = document.getElementById('throughCityUpgrade');
    if (controls) { controls.style.opacity = '1'; controls.style.pointerEvents = 'auto'; }
    if (badge) badge.style.display = 'none';
    if (upgrade) upgrade.style.display = 'none';
};

window.unlockDepartureSlider = function() {
    const pairs = [
        ['departureSliderControls', 'departureSliderProBadge', 'departureSliderUpgrade'],
        ['cardDepartureSliderControls', 'cardDepartureSliderProBadge', 'cardDepartureSliderUpgrade']
    ];
    pairs.forEach(([controlsId, badgeId, upgradeId]) => {
        const controls = document.getElementById(controlsId);
        const badge = document.getElementById(badgeId);
        const upgrade = document.getElementById(upgradeId);
        if (controls) { controls.style.opacity = '1'; controls.style.pointerEvents = 'auto'; }
        if (badge) badge.style.display = 'none';
        if (upgrade) upgrade.style.display = 'none';
    });
};

function departureSliderValueToTimeString(value) {
    const totalMinutes = value * 15;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function departureSliderValueToLabel(value) {
    const totalMinutes = value * 15;
    const hours24 = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const period = hours24 >= 12 ? 'PM' : 'AM';
    let hours12 = hours24 % 12;
    if (hours12 === 0) hours12 = 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

// Set every slider instance to the same value/label (so the desktop panel
// and the mobile card never disagree about the chosen departure time).
function setAllDepartureSliders(value) {
    DEPARTURE_SLIDER_IDS.forEach(({ slider: sliderId, label: labelId }) => {
        const slider = document.getElementById(sliderId);
        const label = document.getElementById(labelId);
        if (slider) slider.value = value;
        if (label) label.textContent = departureSliderValueToLabel(value);
    });
}

// Sync all slider instances to the plan form's actual departure time.
// Called once a route is calculated and the route card / #routeInfo become
// visible, so the slider starts wherever the user already set their
// departure, not a disconnected default.
window.initDepartureSlider = function() {
    const timeInput = document.getElementById('departureTime');
    if (!timeInput || !timeInput.value) return;

    const [h, m] = timeInput.value.split(':').map(Number);
    const value = Math.round((h * 60 + m) / 15);
    setAllDepartureSliders(value);
};

document.addEventListener('DOMContentLoaded', () => {
    let debounceTimer;

    const onSliderInput = (value) => {
        setAllDepartureSliders(value);

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            const timeInput = document.getElementById('departureTime');
            const dateInput = document.getElementById('departureDate');
            if (!timeInput || !dateInput || !window.uiController) return;

            const timeStr = departureSliderValueToTimeString(value);
            timeInput.value = timeStr;

            const dateStr = dateInput.value || new Date().toISOString().split('T')[0];
            const departureDateTime = new Date(`${dateStr}T${timeStr}`);

            // Dim whichever wind displays are currently on screen while the
            // refetch is in flight (desktop trip segments, mobile card details).
            const tripSegments = document.getElementById('tripSegments');
            const cardDetails = document.getElementById('cardDetails');
            if (tripSegments) tripSegments.style.opacity = '0.5';
            if (cardDetails) cardDetails.style.opacity = '0.5';

            try {
                await window.uiController.refreshWindForDepartureTime(departureDateTime);
                // The mobile card's wind row lives in #cardDetails, rebuilt
                // wholesale by updateRouteCard() -- unlike the desktop trip
                // segments, which refreshWindForDepartureTime() already
                // updates directly from cached segments.
                if (window.updateRouteCard) window.updateRouteCard();
            } catch (err) {
                console.error('Departure slider wind refresh failed:', err);
            } finally {
                if (tripSegments) tripSegments.style.opacity = '1';
                if (cardDetails) cardDetails.style.opacity = '1';
            }
        }, 400);
    };

    DEPARTURE_SLIDER_IDS.forEach(({ slider: sliderId }) => {
        const slider = document.getElementById(sliderId);
        if (slider) {
            slider.addEventListener('input', () => onSliderInput(parseInt(slider.value, 10)));
        }
    });
});

// Phase 21: Campground Pro Filters
window.unlockCampgroundFilters = function() {
    const controls = document.getElementById('cgFilterControls');
    const badge = document.getElementById('cgFilterProBadge');
    const upgrade = document.getElementById('cgFilterUpgrade');
    if (controls) { controls.style.opacity = '1'; controls.style.pointerEvents = 'auto'; }
    if (badge) badge.style.display = 'none';
    if (upgrade) upgrade.style.display = 'none';

    // Auto-fill max rig length from profile
    const rigLength = document.getElementById('length')?.value;
    const maxRigInput = document.getElementById('cgf-maxrig');
    if (rigLength && maxRigInput && !maxRigInput.value) {
        maxRigInput.value = rigLength;
    }
};

window.applyCampgroundFilters = function() {
    if (!window.uiController?.currentCampgrounds) return;

    const hookups = document.getElementById('cgf-hookups').checked;
    const pullthru = document.getElementById('cgf-pullthru').checked;
    const showers = document.getElementById('cgf-showers').checked;
    const wifi = document.getElementById('cgf-wifi').checked;
    const pets = document.getElementById('cgf-pets').checked;
    const dump = document.getElementById('cgf-dump').checked;
    const maxRig = parseInt(document.getElementById('cgf-maxrig').value) || 0;

    let filtered = window.uiController.currentCampgrounds.filter(cg => {
        // Australian parks list powered sites but never full hookups, so the
        // same box (worded "Powered sites" there) asks for power.
        if (hookups && !(cg.country === 'AU' ? cg.amenities?.hookups?.electric : cg.amenities?.hookups?.full)) return false;
        if (pullthru && !cg.amenities?.pullThrough) return false;
        if (showers && !cg.amenities?.showers) return false;
        if (wifi && !cg.amenities?.wifi) return false;
        if (pets && !cg.amenities?.petFriendly) return false;
        if (dump && !cg.amenities?.dumpStation) return false;
        if (maxRig > 0 && cg.amenities?.maxRigLengthFt && cg.amenities.maxRigLengthFt < maxRig) return false;
        return true;
    });

    const sort = document.getElementById('cgf-sort').value;
    if (sort === 'rating') {
        filtered.sort((a, b) => {
            const aScore = a.communityRating || (a.verificationCount || 0) / 10;
            const bScore = b.communityRating || (b.verificationCount || 0) / 10;
            return bScore - aScore;
        });
    } else {
        // Sort by distance from route (closest first)
        filtered.sort((a, b) => (a.distanceFromRoute || 99) - (b.distanceFromRoute || 99));
    }

    window.uiController.displayCampgroundMarkers(filtered);
    window.showToast(`Showing ${filtered.length} of ${window.uiController.currentCampgrounds.length} campgrounds`);

    // On mobile, switch to map view to show filtered results
    if (window.innerWidth <= 1024 && window.showMobileMapToggle) {
        window.showMobileMapToggle();
    }
};

// Phase 21: Save Favorite Campground (Pro) -- into the same Favorites list
// the ☰ menu shows (From / To), not a separate profile list nothing reads.
window.saveFavoriteCampground = async function(campgroundId, name) {
    const user = window.authService?.getUser();
    if (!user) return;

    const cg = (window.uiController?.currentCampgrounds || []).find(c => c.campgroundId === campgroundId);
    if (!cg) return;
    const sameSpot = (f) => Math.abs(f.lat - cg.lat) < 0.0001 && Math.abs(f.lng - cg.lng) < 0.0001;
    if (favoritesService.getAll().some(sameSpot)) {
        if (window.showToast) window.showToast(w(`⭐ ${name} is already in your favorites`));
        return;
    }
    const saved = await favoritesService.add(name, cg.lat, cg.lng, cg.address || name);
    if (saved && window.showToast) window.showToast(w(`⭐ ${name} saved to favorites`));
};

// Phase 21: Campground Verification
window.verifyCampgroundFromPopup = async function(campgroundId) {
    const user = window.authService?.getUser();
    if (!user) {
        alert('Please sign in to verify campgrounds.');
        return;
    }

    const API_URL_CG = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

    try {
        const res = await authFetch(`${API_URL_CG}/api/campgrounds/${campgroundId}/verify`, {
            method: 'POST',
            body: JSON.stringify({ campgroundId })
        });
        const data = await res.json();
        alert(data.message || 'Thanks for verifying! 🏕️');
    } catch (err) {
        console.error('Campground verify error:', err);
        alert('Failed to verify. Please try again.');
    }
};

// Phase 21: Campground Community Submission
window.openCampgroundSubmitModal = function() {
    const user = window.authService?.getUser();
    if (!user) {
        alert('Please sign in to submit a campground.');
        return;
    }
    const modal = document.getElementById('campgroundSubmitModal');
    if (modal) modal.style.display = 'flex';
};

window.submitCampgroundForm = async function() {
    const user = window.authService?.getUser();
    if (!user) return;

    const name = document.getElementById('cg-name').value.trim();
    const city = document.getElementById('cg-city').value.trim();
    const state = document.getElementById('cg-state').value.trim().toUpperCase();
    const type = document.getElementById('cg-type').value;
    const notes = document.getElementById('cg-notes').value.trim();

    if (!name || !city || !state) {
        alert('Please fill in name, city, and state.');
        return;
    }

    try {
        const geocodeRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city + ', ' + state)}&format=json&limit=1&countrycodes=us`);
        const geocodeData = await geocodeRes.json();

        if (!geocodeData || geocodeData.length === 0) {
            alert('Could not find that location. Please check the city and state.');
            return;
        }

        const lat = parseFloat(geocodeData[0].lat);
        const lng = parseFloat(geocodeData[0].lon);

        const amenities = {
            hookups: {
                electric: document.getElementById('cg-electric').checked,
                water: document.getElementById('cg-water').checked,
                sewer: document.getElementById('cg-sewer').checked,
                full: document.getElementById('cg-electric').checked && document.getElementById('cg-water').checked && document.getElementById('cg-sewer').checked
            },
            pullThrough: document.getElementById('cg-pullthru').checked,
            showers: document.getElementById('cg-showers').checked,
            wifi: document.getElementById('cg-wifi').checked,
            dumpStation: document.getElementById('cg-dump').checked,
            petFriendly: document.getElementById('cg-pets').checked
        };

        const API_URL_CG = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

        const res = await authFetch(`${API_URL_CG}/api/campgrounds`, {
            method: 'POST',
            body: JSON.stringify({
                name,
                lat,
                lng,
                type,
                amenities,
                notes: notes || null
            })
        });

        const data = await res.json();
        document.getElementById('campgroundSubmitModal').style.display = 'none';

        document.getElementById('cg-name').value = '';
        document.getElementById('cg-city').value = '';
        document.getElementById('cg-state').value = '';
        document.getElementById('cg-notes').value = '';
        document.querySelectorAll('#campgroundSubmitModal input[type=checkbox]').forEach(cb => cb.checked = false);

        alert(data.message || 'Campground submitted! It will appear after community verification. 🏕️');
    } catch (err) {
        console.error('Campground submission error:', err);
        alert('Failed to submit campground. Please try again.');
    }
};

window.openRoadReportModal = function() {
    const user = window.authService?.getUser();
    if (!user) {
        alert('Please sign in to report road hazards.');
        return;
    }
    if (!window.uiController?.currentRoutes) {
        alert('Please calculate a route first.');
        return;
    }
    const modal = document.getElementById('roadReportModal');
    if (modal) {
        modal.style.display = 'flex';
        // Reset state
        document.querySelectorAll('.hazard-btn').forEach(btn => {
            btn.style.borderColor = '#e0e0e0';
            btn.style.background = 'white';
        });
        document.getElementById('hazardDetails').style.display = 'none';
        document.getElementById('clearanceField').style.display = 'none';
        document.getElementById('rr-notes').value = '';
        document.getElementById('rr-clearance').value = '';
        window._selectedHazardType = null;
        // "Where I am" only on phones/tablets; a computer's location is too rough.
        const hereBtn = document.getElementById('rrHereBtn');
        if (hereBtn) hereBtn.style.display = window.matchMedia('(pointer: coarse)').matches ? 'block' : 'none';
    }
    // Wire up hazard type buttons
    document.querySelectorAll('.hazard-btn').forEach(btn => {
        btn.onclick = function() {
            document.querySelectorAll('.hazard-btn').forEach(b => {
                b.style.borderColor = '#e0e0e0';
                b.style.background = 'white';
            });
            this.style.borderColor = '#1e88e5';
            this.style.background = '#e3f2fd';
            window._selectedHazardType = this.dataset.type;
            document.getElementById('hazardDetails').style.display = 'block';
            document.getElementById('clearanceField').style.display =
                this.dataset.type === 'low_clearance' ? 'block' : 'none';
        };
    });
};

// Phones report where they are (GPS); any device can pick the spot on the
// map instead (also for a hazard already driven past).
window.submitRoadReport = async function(where) {
    const user = window.authService?.getUser();
    if (!user || !window._selectedHazardType) return;
    const report = {
        reportType: window._selectedHazardType,
        notes: document.getElementById('rr-notes').value.trim() || null,
        clearanceFt: document.getElementById('rr-clearance').value ? Number(document.getElementById('rr-clearance').value) : null
    };
    const modal = document.getElementById('roadReportModal');

    const pickOnMap = () => {
        const map = window.uiController?.mapService?.map;
        if (!map) return;
        if (modal) modal.style.display = 'none';
        if (window.innerWidth <= 1024 && window.showMobileMapToggle) window.showMobileMapToggle();
        if (window.showToast) window.showToast('Tap the map where the hazard is', { duration: 8000 });
        map.getCanvas().style.cursor = 'crosshair';
        map.once('click', (e) => {
            map.getCanvas().style.cursor = '';
            sendRoadReport(report, e.lngLat.lat, e.lngLat.lng);
        });
    };

    if (where === 'map') { pickOnMap(); return; }
    if (!navigator.geolocation) { pickOnMap(); return; }
    if (window.showToast) window.showToast('Finding your location…');
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            if (modal) modal.style.display = 'none';
            sendRoadReport(report, pos.coords.latitude, pos.coords.longitude);
        },
        () => {
            if (window.showToast) window.showToast("Couldn't get your location. Pick the spot on the map instead.");
            pickOnMap();
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
};

async function sendRoadReport(report, lat, lng) {
    try {
        const res = await authFetch(`${API_URL_RR}/api/road-reports`, {
            method: 'POST',
            body: JSON.stringify({ ...report, lat, lng })
        });
        const data = await res.json();
        alert(data.message || 'Report submitted. Thanks!');
        // Show it on this route straight away.
        const ui = window.uiController;
        const routeIndex = ui?.selectedRouteIndex !== undefined ? ui.selectedRouteIndex : ui?.bestRouteIndex;
        const route = ui?.currentRoutes?.[routeIndex];
        if (route) ui.loadRoadReports(route.coordinates);
    } catch (err) {
        console.error('Road report error:', err);
        alert('Failed to submit report. Try again later.');
    }
}

// Phase 8: Fuel stop verification
window.verifyFuelStop = async function(fuelStopId, stationName) {
    const user = window.authService?.getUser();
    if (!user) {
        alert('Please sign in to verify fuel stops.');
        return;
    }

    // Show inline verification UI instead of confirm() dialogs
    const modal = document.getElementById('fuelVerifyModal');
    if (modal) {
        document.getElementById('fv-station-name').textContent = stationName;
        // Reset selections
        document.querySelectorAll('.fv-btn').forEach(b => {
            b.style.background = 'white';
            b.style.color = '#333';
        });
        modal.style.display = 'flex';
        window._fuelVerifyData = { fuelStopId, stationName, userId: user.sub };
    }
};

window.selectFuelVerify = function(field, value, btn) {
    // Highlight selected button, deselect sibling
    const row = btn.parentElement;
    row.querySelectorAll('.fv-btn').forEach(b => {
        b.style.background = 'white';
        b.style.color = '#333';
    });
    btn.style.background = value ? '#4CAF50' : '#e53935';
    btn.style.color = 'white';
    window._fuelVerifyData[field] = value;
};

window.submitFuelVerification = async function() {
    const data = window._fuelVerifyData;
    if (data.isOpen === undefined || data.hasPullThrough === undefined || data.hasDiesel === undefined) {
        alert('Please answer all three questions.');
        return;
    }

    try {
        await authFetch(`${API_URL_FB}/api/fuel-stops/verify`, {
            method: 'POST',
            body: JSON.stringify({
                userId: data.userId,
                fuelStopId: data.fuelStopId || data.stationName,
                isOpen: data.isOpen,
                hasPullThrough: data.hasPullThrough,
                hasDiesel: data.hasDiesel
            })
        });
        document.getElementById('fuelVerifyModal').style.display = 'none';
        alert(w('Thanks for verifying! 🙏 Your feedback helps other RV travelers.'));
    } catch (err) {
        console.error('Fuel verify error:', err);
        alert('Failed to submit verification. Try again later.');
    }
};

console.log('Services loaded:', {
    RVProfileService: !!window.RVProfileService,
    userProfileService: !!window.userProfileService
});

// Initialize services
const mapService = new MapService('map');
const elevationService = new ElevationService();
const windService = new WindService();
const routeService = new RouteService();
const gasStationService = new GasStationService();
const clearanceService = new ClearanceService();
const roadQualityService = new RoadQualityService();
const campgroundService = new CampgroundService();

// Make routeTrackingService available globally
window.routeTrackingService = routeTrackingService;

const uiController = new UIController({
    mapService,
    elevationService,
    windService,
    routeService,
    gasStationService,
    clearanceService,
    roadQualityService,
    campgroundService
});
window.uiController = uiController;
initRouteCardLayers();

/**
 * Update the sign in/out buttons and user display
 */
async function updateAuthUI() {
    const authButtons = document.getElementById('authButtons');
    const userMenu = document.getElementById('userMenu');
    const userEmail = document.getElementById('userEmail');
    const subscribeBtn = document.getElementById('subscribeBtn');

    if (!authButtons || !userMenu) return;

    // "Didn't finish signing up?" -- only for a signed-out person who started
    // sign-up on this device (see signupUnfinished in AuthService).
    const finishSignup = document.getElementById('finishSignupBanner');
    if (authService.isSignedIn()) clearSignupStarted();
    if (finishSignup) finishSignup.style.display = !authService.isSignedIn() && signupUnfinished() ? 'flex' : 'none';

    if (authService.isSignedIn()) {
        const user = authService.getUser();
        authButtons.style.display = 'none';
        userMenu.style.display = 'flex';
        if (userEmail && user) {
            userEmail.textContent = user.email;
        }

        // Fetch user profile and check for founding member status
        if (user) {
            const profile = await userProfileService.getProfile(user.sub);
            if (profile) {
                applyAccount(profile);

                // Update display name if available
                const userEmailElement = document.getElementById('userEmail');
                if (userEmailElement && profile.displayName) {
                    userEmailElement.textContent = profile.displayName;
                }
                
                // Show founding member badge
                if (profile.isFoundingMember) {
                    const foundingBadge = document.getElementById('foundingMemberBadge');
                    if (foundingBadge) {
                        foundingBadge.textContent = `⭐ Founding Member`;
                        foundingBadge.style.display = 'inline';
                    }
                }
                
                // Auto-fill RV specs if saved in profile
                if (profile.rvSpecs) {
                    console.log('🚐 Auto-filling RV specs from profile:', profile.rvSpecs);
                    const rvTypeEl = document.getElementById('rvType');
                    const heightEl = document.getElementById('height');
                    const weightEl = document.getElementById('weight');
                    const lengthEl = document.getElementById('length');
                    
                    if (rvTypeEl && profile.rvSpecs.type) rvTypeEl.value = profile.rvSpecs.type;
                    if (heightEl && profile.rvSpecs.height) heightEl.value = profile.rvSpecs.height;
                    if (weightEl && profile.rvSpecs.weight) weightEl.value = profile.rvSpecs.weight;
                    if (lengthEl && profile.rvSpecs.length) lengthEl.value = profile.rvSpecs.length;
                    
                    // Auto-fill tow vehicle if trailer type
                    const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(profile.rvSpecs.type);
                    if (isTrailer && profile.rvSpecs.towVehicle) {
                        const towVehicleTypeEl = document.getElementById('towVehicleType');
                        const towVehicleEngineEl = document.getElementById('towVehicleEngine');
                        const towCapacityEl = document.getElementById('towCapacity');
                        const towVehicleWeightEl = document.getElementById('towVehicleWeight');
                        const hasSwayControlEl = document.getElementById('hasSwayControl');
                        const hasTrailerBrakesEl = document.getElementById('hasTrailerBrakes');
                        
                        if (towVehicleTypeEl && profile.rvSpecs.towVehicle.type) towVehicleTypeEl.value = profile.rvSpecs.towVehicle.type;
                        if (towVehicleEngineEl && profile.rvSpecs.towVehicle.engine) towVehicleEngineEl.value = profile.rvSpecs.towVehicle.engine;
                        if (towCapacityEl && profile.rvSpecs.towVehicle.capacity) towCapacityEl.value = profile.rvSpecs.towVehicle.capacity;
                        if (towVehicleWeightEl && profile.rvSpecs.towVehicle.weight) towVehicleWeightEl.value = profile.rvSpecs.towVehicle.weight;
                        if (hasSwayControlEl) hasSwayControlEl.checked = profile.rvSpecs.towVehicle.hasSwayControl || false;
                        if (hasTrailerBrakesEl) hasTrailerBrakesEl.checked = profile.rvSpecs.towVehicle.hasTrailerBrakes || false;
                        
                        // Show tow vehicle section
                        const towVehicleSection = document.getElementById('towVehicleSection');
                        if (towVehicleSection) {
                            towVehicleSection.classList.remove('hidden');
                        }
                    }
                    
                    console.log('✅ RV specs auto-filled');
                } else {
                    console.log('ℹ️ No RV specs in profile');
                }

                // Phase 6: Wire travel preferences to route planning
                if (profile.travelPreferences) {
                    const prefs = profile.travelPreferences;
                    
                    // Task 13: Pre-fill departure time from profile
                    if (prefs.preferredDepartureTime) {
                        const departureTimeEl = document.getElementById('departureTime');
                        const departureDateEl = document.getElementById('departureDate');
                        if (departureTimeEl && departureDateEl) {
                            const today = new Date().toISOString().split('T')[0];
                            // Only override if date is in the future (not today)
                            if (departureDateEl.value !== today) {
                                departureTimeEl.value = prefs.preferredDepartureTime;
                            }
                        }
                    }

                    // Store travel prefs globally for route calculation
                    window.travelPreferences = prefs;
                    console.log('⚙️ Travel preferences loaded:', prefs);
                }
            }
        }

        // Check subscription status
        if (user && subscribeBtn) {
            // Once per app start: re-send this phone's Google Play purchases so
            // renewals and reinstalls keep Pro on before the status check.
            if (playBillingService.available() && !window._playPurchasesSynced) {
                window._playPurchasesSynced = true;
                try {
                    if ((await playBillingService.syncPurchases()) !== null) subscriptionService.clearCache();
                } catch (e) {
                    console.warn('Play purchase sync failed:', e?.message || e);
                }
            }
            const isSubscribed = await subscriptionService.isSubscribed(user.sub);
            if (isSubscribed) {
                subscribeBtn.style.display = 'none';
                window.isSubscribed = true;
                const badge = document.getElementById('subscriptionBadge');
                if (badge) badge.style.display = 'inline';
                // Phase 21: Unlock campground filters for Pro users
                if (window.unlockCampgroundFilters) window.unlockCampgroundFilters();
                // Unlock Saved Routes for Pro users
                if (window.unlockSavedRoutes) window.unlockSavedRoutes();
                // Unlock the departure time slider for Pro users
                if (window.unlockDepartureSlider) window.unlockDepartureSlider();
                if (window.unlockAvoidCities) window.unlockAvoidCities();
                if (window.unlockThroughCities) window.unlockThroughCities();
            } else {
                subscribeBtn.style.display = canPurchaseInApp() ? 'inline-block' : 'none';
                window.isSubscribed = false;
            }
            
            // Load route status for free users
            if (!isSubscribed) {
                const status = await routeTrackingService.getRouteStatus(user.sub);
                // A brand-new account has no profile until route-status creates it.
                applyAccountMarketHint(status?.market);
            }
            
            // Update usage display after subscription status is known
            if (window.uiController) window.uiController.updateUsageDisplay();
        }

        // Phase 24: Initialize push notifications (Capacitor native only)
        if (user) {
            pushNotificationService.init(user.sub);
            // Phase 28: Initialize notification center
            notificationCenterService.init(user.sub);
        }
    } else {
        clearAccountMarket();
        authButtons.style.display = 'block';
        userMenu.style.display = 'none';
        window.isSubscribed = false;
        if (subscribeBtn) subscribeBtn.style.display = 'none';
        if (window.uiController) window.uiController.updateUsageDisplay();
    }
}

// Initialize the application
document.addEventListener('DOMContentLoaded', async () => {
    console.log('DOM Content Loaded');
    console.log('RV Navigator initializing...');

    // Fire-and-forget: get autocomplete's location bias ready ASAP, well
    // before the user starts typing a search.
    primeLocationBiasInBackground();

    // Handle Cognito callback if returning from login
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.get('code');
    const hasState = urlParams.get('state');
    const hasPkceVerifier = localStorage.getItem('pkce_verifier');

    if (hasCode && (hasState || hasPkceVerifier)) {
        // Cognito OAuth callback (has state param from PKCE flow)
        await authService.handleCallback();
    } else if (hasCode && !hasState && !hasPkceVerifier) {
        // Facebook OAuth callback (code without PKCE state)
        window.history.replaceState({}, document.title, window.location.pathname);
        try {
            const fbRes = await fetch(`${API_URL_FB}/api/auth/facebook`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: hasCode })
            });
            const fbData = await fbRes.json();
            if (fbData.status === 'success') {
                // Facebook user verified — sign them into Cognito with their email
                alert(`Welcome ${fbData.name}! Your Facebook account (${fbData.email}) is linked. Please sign in with this email to complete setup.`);
                await authService.signIn();
            } else {
                alert('Facebook login failed: ' + (fbData.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Facebook auth error:', err);
            alert('Facebook login failed. Please try again.');
        }
    } else if (urlParams.get('signin') === 'true' && !authService.isSignedIn()) {
        // Redirect to Cognito login immediately — this generates PKCE and redirects
        window.history.replaceState({}, document.title, window.location.pathname);
        await authService.signIn();
        return; // Stop here, page will redirect to Cognito
    } else {
        // Silently refresh token if needed
        await authService.refreshIfNeeded();
    }

    // Handle post-Stripe checkout redirect
    if (urlParams.get('subscribed') === 'true') {
        subscriptionService.clearCache();
        userProfileService.clearCache(); // Clear profile cache to fetch fresh data
        
        // Wait a moment for webhook to process, then fetch profile
        setTimeout(async () => {
            const user = authService.getUser();
            if (user) {
                const profile = await userProfileService.getProfile(user.sub);
                
                // Update display name
                const userEmailElement = document.getElementById('userEmail');
                if (userEmailElement && profile && profile.displayName) {
                    userEmailElement.textContent = profile.displayName;
                }
                
                if (profile && profile.isFoundingMember) {
                    alert(`🎉 Welcome to Zephyr Pro, Founding Member!\n\nYou're one of the first 100 members and now have unlimited route calculations.`);
                } else {
                    alert('🎉 Welcome to Zephyr Pro! You now have unlimited route calculations.');
                }
                // Refresh UI to show founding member badge
                await updateAuthUI();
            } else {
                alert('🎉 Welcome to Zephyr Pro! You now have unlimited route calculations.');
            }
        }, 1000);
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Update auth UI and wait for subscription check to complete
    await updateAuthUI();
    // Load favorites after auth. Uses refreshIfNeeded() instead of the
    // plain isSignedIn() check -- isSignedIn() only checks token expiry with
    // no refresh, while FavoritesService.load()'s actual request goes
    // through authFetch(), which DOES refresh an expiring token first. That
    // mismatch meant an expired-but-refreshable token at cold start made
    // this gate conclude "not signed in" and skip loading favorites for the
    // whole session, silently, even though every other authFetch-based
    // feature would have refreshed and worked fine.
    if (await authService.refreshIfNeeded()) {
        await favoritesService.load();
        await savedRoutesService.load();
    }

    // Sign-In Flow: Restore pending route after sign-in/sign-up
    if (authService.isSignedIn() && window.restorePendingRoute) {
        const restored = window.restorePendingRoute();
        if (restored) {
            // Auto-trigger Calculate after a short delay so UI is ready
            setTimeout(() => {
                const calculateBtn = document.getElementById('calculateRoute');
                if (calculateBtn) {
                    console.log('📋 Auto-triggering Calculate for restored route');
                    calculateBtn.click();
                }
            }, 500);
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // Onboarding Wizard: Redirect new users who haven't completed setup
    // Triggers once after first sign-up. Never shows again after completion.
    // ═══════════════════════════════════════════════════════════════
    if (authService.isSignedIn()) {
        const onboardingDoneLocal = localStorage.getItem('zephyr_onboarding_complete');
        if (!onboardingDoneLocal) {
            const user = authService.getUser();
            if (user) {
                const profile = await userProfileService.getProfile(user.sub);
                const onboardingDone = profile?.onboardingComplete === true;
                const hasRvSpecs = profile?.rvSpecs?.type;
                if (!onboardingDone && !hasRvSpecs) {
                    console.log('🧭 New user detected — redirecting to onboarding wizard');
                    window.location.href = pagePath('/onboarding.html');
                    return;
                } else {
                    // Mark locally so we skip the profile check next time
                    localStorage.setItem('zephyr_onboarding_complete', 'true');
                }
            }
        }
    }

    // Clear profile cache if returning from profile page (referrer check)
    if (document.referrer.includes('/profile.html')) {
        console.log('🔄 Returning from profile page - clearing cache');
        const user = authService.getUser();
        if (user) {
            userProfileService.clearCache();
            // Reload profile and update UI
            const profile = await userProfileService.getProfile(user.sub);
            if (profile && profile.rvSpecs) {
                console.log('🚐 Updating RV specs after profile page visit:', profile.rvSpecs);
                const rvTypeEl = document.getElementById('rvType');
                const heightEl = document.getElementById('height');
                const weightEl = document.getElementById('weight');
                const lengthEl = document.getElementById('length');
                
                if (rvTypeEl && profile.rvSpecs.type) rvTypeEl.value = profile.rvSpecs.type;
                if (heightEl && profile.rvSpecs.height) heightEl.value = profile.rvSpecs.height;
                if (weightEl && profile.rvSpecs.weight) weightEl.value = profile.rvSpecs.weight;
                if (lengthEl && profile.rvSpecs.length) lengthEl.value = profile.rvSpecs.length;
                
                // Update display name too
                if (profile.displayName) {
                    const userEmailElement = document.getElementById('userEmail');
                    if (userEmailElement) {
                        userEmailElement.textContent = profile.displayName;
                    }
                }
            }
        }
    }

    // Refresh display name and RV specs when page becomes visible (e.g., returning from profile page)
    document.addEventListener('visibilitychange', async () => {
        if (!document.hidden) {
            console.log('👁️ Page visibility changed - refreshing profile');
            const user = authService.getUser();
            if (user) {
                // Always fetch fresh profile when returning to page
                userProfileService.clearCache();
                const profile = await userProfileService.getProfile(user.sub);
                
                if (profile) {
                    console.log('🔄 Updating UI with fresh profile data');
                    // Update display name
                    if (profile.displayName) {
                        const userEmailElement = document.getElementById('userEmail');
                        if (userEmailElement) {
                            userEmailElement.textContent = profile.displayName;
                        }
                    }
                    
                    // Update RV specs if saved
                    if (profile.rvSpecs) {
                        console.log('🚐 Updating RV specs from fresh profile:', profile.rvSpecs);
                        const rvTypeEl = document.getElementById('rvType');
                        const heightEl = document.getElementById('height');
                        const weightEl = document.getElementById('weight');
                        const lengthEl = document.getElementById('length');
                        
                        if (rvTypeEl && profile.rvSpecs.type) rvTypeEl.value = profile.rvSpecs.type;
                        if (heightEl && profile.rvSpecs.height) heightEl.value = profile.rvSpecs.height;
                        if (weightEl && profile.rvSpecs.weight) weightEl.value = profile.rvSpecs.weight;
                        if (lengthEl && profile.rvSpecs.length) lengthEl.value = profile.rvSpecs.length;
                        
                        // Update tow vehicle if trailer type
                        const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(profile.rvSpecs.type);
                        if (isTrailer && profile.rvSpecs.towVehicle) {
                            const towVehicleTypeEl = document.getElementById('towVehicleType');
                            const towVehicleEngineEl = document.getElementById('towVehicleEngine');
                            const towCapacityEl = document.getElementById('towCapacity');
                            const towVehicleWeightEl = document.getElementById('towVehicleWeight');
                            const hasSwayControlEl = document.getElementById('hasSwayControl');
                            const hasTrailerBrakesEl = document.getElementById('hasTrailerBrakes');
                            
                            if (towVehicleTypeEl && profile.rvSpecs.towVehicle.type) towVehicleTypeEl.value = profile.rvSpecs.towVehicle.type;
                            if (towVehicleEngineEl && profile.rvSpecs.towVehicle.engine) towVehicleEngineEl.value = profile.rvSpecs.towVehicle.engine;
                            if (towCapacityEl && profile.rvSpecs.towVehicle.capacity) towCapacityEl.value = profile.rvSpecs.towVehicle.capacity;
                            if (towVehicleWeightEl && profile.rvSpecs.towVehicle.weight) towVehicleWeightEl.value = profile.rvSpecs.towVehicle.weight;
                            if (hasSwayControlEl) hasSwayControlEl.checked = profile.rvSpecs.towVehicle.hasSwayControl || false;
                            if (hasTrailerBrakesEl) hasTrailerBrakesEl.checked = profile.rvSpecs.towVehicle.hasTrailerBrakes || false;
                            
                            // Show tow vehicle section
                            const towVehicleSection = document.getElementById('towVehicleSection');
                            if (towVehicleSection) {
                                towVehicleSection.classList.remove('hidden');
                            }
                        }
                    }
                }
            }
        }
    });
    
    // Also refresh on pageshow event (more reliable for back navigation)
    window.addEventListener('pageshow', async (event) => {
        // Only refresh if coming from cache (back button)
        if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
            const user = authService.getUser();
            if (user) {
                userProfileService.clearCache();
                const profile = await userProfileService.getProfile(user.sub);
                
                if (profile && profile.displayName) {
                    const userEmailElement = document.getElementById('userEmail');
                    if (userEmailElement) {
                        userEmailElement.textContent = profile.displayName;
                    }
                }
            }
        }
    });

    try {
        uiController.init();
        console.log('RV Navigator initialized successfully');
        
        // Phase 4.5: Attach autocomplete to location inputs
        autocompleteService.attach('start');
        autocompleteService.attach('destination');
        autocompleteService.attach('avoidCityInput', { onSelect: (place) => uiController.addAvoidCity(place) });
        autocompleteService.attach('throughCityInput', { onSelect: (place) => uiController.addThroughCity(place) });
        window.autocompleteService = autocompleteService;
        
        // Make selectRoute available globally for onclick handlers
        window.selectRoute = (index) => uiController.selectRoute(index);

        // Phase 15: Tap map to collapse expanded route card
        if (uiController.mapService && uiController.mapService.map) {
            uiController.mapService.map.on('click', () => {
                const expanded = document.getElementById('routeCardExpanded');
                if (expanded && !expanded.classList.contains('hidden')) {
                    window.collapseRouteCard();
                }
            });
        }

        // Phase 7: Check for pending trip feedback on app load
        if (window.checkPendingTripFeedback) {
            setTimeout(() => window.checkPendingTripFeedback(), 5000);
            // Also check when page becomes visible (mobile browsers suspend tabs)
            document.addEventListener('visibilitychange', () => {
                if (!document.hidden && window.checkPendingTripFeedback) {
                    window.checkPendingTripFeedback();
                }
            });
        }
    } catch (error) {
        console.error('Error initializing RV Navigator:', error);
    }
});
